/** History page model layer — thin consumer of the shared backend-mirror.
 *
 * Layer: `model` per docs/design/spa-account-extraction-260524.md §3.5
 * and tools/lint/lint-frontend-layer-imports.py SPA_HISTORY_LAYERS.
 *
 * Per the design doc §7 ratification: the History page is a VIEW over
 * the same backend-owned model (`/api/account/jobs`) that
 * `spa/job-state.ts` (SSE-push) and `/account` (pull) consume. The
 * duplication is deliberate — both pages mirror the backend's job-list
 * state. This module is the on-demand-pull side; SSE-push lives in
 * `spa/job-state.ts` and coexists.
 *
 * Cross-tree import policy (per design doc §7 + lint cross-tree allow-
 * matrix): the model layer is the ONLY layer in `spa-history/` allowed
 * to import from `spa/backend-mirror.js`. Views and controllers go
 * through this module.
 *
 * AC.7 establishes the pattern only — wiring of the History page's
 * controller / view onto these registrations is AC.8 / AC.9.
 */
import { backendMirror } from '../spa/backend-mirror.js';
/** Re-export so the controller layer can call backendMirror.mutate() without
 *  a cross-tree import violation. Per lint cross-tree allow-matrix:
 *  only ("spa-history","model") may import from ("spa","backend-mirror.ts");
 *  controller goes through this re-export. */
export { backendMirror } from '../spa/backend-mirror.js';
/** Page size matches `history.html:227` (`const perPage = 25`).
 *  Lives here so future tuning is a single-line change. */
export const HISTORY_PER_PAGE = 25;
/** Query-key constructor for a given page index. Path-like tuple so
 *  `invalidate(['history', 'jobs'])` invalidates ALL pages at once
 *  (used after a delete / clear-all). */
export function keyJobs(page, perPage = HISTORY_PER_PAGE) {
    return ['history', 'jobs', { page, perPage }];
}
/** Register the first-page query against the shared backend-mirror.
 *  AC.8 will register additional pages as `loadMore` advances. */
export function registerHistoryJobsPage(authFetch, page = 1) {
    backendMirror.register(keyJobs(page), async () => {
        const resp = await authFetch(`/api/account/jobs?per_page=${HISTORY_PER_PAGE}&page=${page}`);
        if (!resp.ok)
            throw new Error(`HTTP ${resp.status}`);
        return resp.json();
    });
}
/** Mutation spec: delete a single job. Optimistic update removes the row
 *  from page-1's cached data immediately; rollback restores on error.
 *  After success, all `['history', 'jobs', ...]` pages are invalidated
 *  so totals re-sync from the server. */
export function deleteJobMutation(authFetch) {
    return {
        perform: async (jobId) => {
            const resp = await authFetch(`/api/account/jobs/${jobId}`, { method: 'DELETE' });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
        },
        invalidates: [['history', 'jobs']],
        optimistic: {
            key: keyJobs(1),
            produce: (current, jobId) => {
                const page = current;
                if (!page)
                    return { jobs: [], jobs_total: 0 };
                return {
                    ...page,
                    jobs: page.jobs.filter(j => j.id !== jobId),
                    jobs_total: typeof page.jobs_total === 'number'
                        ? Math.max(0, page.jobs_total - 1)
                        : page.jobs_total,
                };
            },
        },
    };
}
/** Mutation spec: clear all completed/failed jobs.
 *  Invalidates the full `['history', 'jobs', ...]` subtree on success. */
export function clearHistoryMutation(authFetch) {
    return {
        perform: async () => {
            const resp = await authFetch('/api/account/jobs/clear', { method: 'POST' });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
        },
        invalidates: [['history', 'jobs']],
    };
}
/** Mutation spec: delete every file the user owns. Invalidates the full
 *  jobs subtree on success. */
export function deleteAllFilesMutation(authFetch) {
    return {
        perform: async () => {
            const resp = await authFetch('/api/account/jobs/delete-all', { method: 'POST' });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
        },
        invalidates: [['history', 'jobs']],
    };
}
